export type Trend = "up" | "down" | "flat";

export interface Category {
  id: string;
  label: string;
  icon: string;
}

export interface HotEvent {
  id: string;
  title: string;
  summary: string;
  cover: string;
  cat: string;
  catLabel: string;
  tags: string[];
  heat: number;
  trend: Trend;
  time: string;
  featured?: boolean;
  featuredBody?: string[];
  highlight?: string[];
}

export interface SurgeItem {
  title: string;
  delta: number;
  catLabel: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface PrincipleItem {
  icon: string;
  title: string;
  desc: string;
}

export const siteName = "吃瓜榜单";
export const siteSlogan = "把热乎的瓜，摆上明面";

export const navLinks = [
  { label: "今日热榜", href: "#rank" },
  { label: "瓜田事件", href: "#feed" },
  { label: "专题深度", href: "#feature" },
  { label: "吃瓜态度", href: "#philosophy" },
  { label: "常见问题", href: "#faq" },
];

export const categories: Category[] = [
  { id: "drama", label: "影视热剧", icon: "ri-film-line" },
  { id: "variety", label: "综艺现场", icon: "ri-tv-2-line" },
  { id: "word", label: "热搜词条", icon: "ri-fire-line" },
  { id: "brand", label: "品牌话题", icon: "ri-cup-line" },
  { id: "music", label: "音乐现场", icon: "ri-mic-line" },
  { id: "society", label: "社会热议", icon: "ri-earth-line" },
];

export const events: HotEvent[] = [
  {
    id: "ev-drama-year",
    title: "年度古装剧收官夜：那些出圈台词与名场面",
    summary:
      "一部年度古装剧迎来收官，剧粉连夜二刷截图，#台词高光# 与 #大结局  # 词条同步霸榜，网友笑称“舍不得点开最后一集”。",
    cover:
      "https://readdy.ai/api/search-image?query=Ancient%20Chinese%20costume%20drama%20moody%20scene%20with%20flowing%20crimson%20ribbons%20and%20warm%20lantern%20light%2C%20cinematic%20editorial%20poster%20illustration%2C%20cream%20and%20watermelon%20red%20tones%2C%20elegant%20dramatic%2C%20no%20text&width=800&height=560&seq=ev-drama&orientation=landscape",
    cat: "drama",
    catLabel: "影视热剧",
    tags: ["台词出圈", "收官", "剧粉"],
    heat: 982,
    trend: "up",
    time: "2026-09-06 21:18",
    featured: true,
    featuredBody: [
      "收官夜，#台词高光# 与 #大结局# 两个词条先后冲上热搜前列，相关话题阅读量在当晚突破数十亿次。比起以往“结局党”与“剧情党”的对立，这一次讨论更多落在对白与表演层面，被网友形容为“可以反复品的一集”。",
      "从角色弧光到服化道的细节考据，考据党们连夜整理了前后呼应的镜头语言，把原本停留在观感的追剧体验，拉长成一场持续数日的公共讨论。平台顺势上线了台词金句的专题入口，方便新观众快速入坑。",
      "值得注意的是，剧方在收官后发布了一条“主创手记”，回应了部分关于改编尺度的疑问，也让围绕结局的争议回归到创作讨论本身。理性看待、不互相拉踩，成为这波热度里的主流声音。",
    ],
    highlight: [
      "#台词高光# 单小时讨论峰值破百万条",
      "大结局当晚二刷率较首播日上涨约三成",
      "考据党连续三天整理镜头语言伏笔盘点",
    ],
  },
  {
    id: "ev-variety-slow",
    title: "老牌慢综回归：治愈慢下来还是被嫌太拖",
    summary:
      "一档治愈系慢综艺官宣新一季，官博评论区两边倒——有人直呼“下饭神综回来了”，也有人嫌节奏拖沓，讨论持续走高。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cozy%20variety%20show%20scenery%20of%20friends%20gathering%20in%20a%20sunlit%20countryside%20yard%20with%20tea%20and%20food%2C%20soft%20cream%20and%20amber%20tones%2C%20gentle%20illustrative%20editorial%20style%2C%20no%20text&width=800&height=560&seq=ev-variety&orientation=landscape",
    cat: "variety",
    catLabel: "综艺现场",
    tags: ["慢综艺", "回归", "下饭"],
    heat: 874,
    trend: "up",
    time: "2026-09-06 19:02",
    highlight: [],
  },
  {
    id: "ev-music-fest",
    title: "音乐节官宣阵容引爆退票讨论",
    summary:
      "多地音乐节集中官宣阵容，票价与舞台安排成为焦点，#音乐节该不该为情怀买单# 引发关于观演体验的大讨论。",
    cover:
      "https://readdy.ai/api/search-image?query=Outdoor%20music%20festival%20scene%20at%20dusk%20with%20warm%20stage%20lights%20and%20silhouetted%20crowd%2C%20amber%20and%20crimson%20palette%2C%20stylish%20editorial%20illustration%2C%20no%20text&width=800&height=560&seq=ev-music&orientation=landscape",
    cat: "music",
    catLabel: "音乐现场",
    tags: ["音乐节", "阵容", "观演体验"],
    heat: 791,
    trend: "flat",
    time: "2026-09-06 17:40",
    highlight: [],
  },
  {
    id: "ev-brand-tea",
    title: "新茶饮联名又刷屏：排队三小时到底值不值",
    summary:
      "一杯联名饮品带火话题，#为联名买单的年轻人# 冲上热搜，有人晒单排了三小时，也有人劝退：理性消费才是真香。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylish%20milk%20tea%20cup%20with%20creative%20collaboration%20packaging%20on%20a%20cream%20table%2C%20warm%20background%2C%20modern%20product%20editorial%20photography%20style%2C%20no%20text&width=800&height=560&seq=ev-brand&orientation=landscape",
    cat: "brand",
    catLabel: "品牌话题",
    tags: ["联名", "排队", "理性消费"],
    heat: 762,
    trend: "up",
    time: "2026-09-06 16:11",
    highlight: [],
  },
  {
    id: "ev-word-citywalk",
    title: "“城市漫步”走红：年轻人的周末打开方式",
    summary:
      "从“特种兵式旅游”到慢慢逛的 citywalk，年轻人开始反内卷。#城市漫步你逛了吗# 话题下，晒图攻略不断刷屏。",
    cover:
      "https://readdy.ai/api/search-image?query=Young%20people%20strolling%20through%20a%20charming%20historic%20city%20street%20in%20warm%20evening%20light%2C%20illustrative%20editorial%20style%2C%20cream%20and%20amber%20tones%2C%20no%20text&width=800&height=560&seq=ev-word&orientation=landscape",
    cat: "word",
    catLabel: "热搜词条",
    tags: ["城市漫步", "反内卷", "周末"],
    heat: 689,
    trend: "up",
    time: "2026-09-06 15:05",
    highlight: [],
  },
  {
    id: "ev-variety-cp",
    title: "恋综观察室：磕 CP 与保持理性的平衡",
    summary:
      "恋综新一集上线，观察室的发言成为讨论焦点。网友在“沉浸式磕糖”与“别当真”之间反复横跳，话题热度居高不下。",
    cover:
      "https://readdy.ai/api/search-image?query=Dating%20variety%20show%20studio%20scene%20with%20warm%20stage%20lighting%20and%20heart%20shaped%20decor%2C%20cream%20and%20soft%20red%20tones%2C%20stylish%20editorial%20illustration%2C%20no%20text&width=800&height=560&seq=ev-cp&orientation=landscape",
    cat: "variety",
    catLabel: "综艺现场",
    tags: ["恋综", "观察室", "磕CP"],
    heat: 655,
    trend: "down",
    time: "2026-09-06 14:22",
    highlight: [],
  },
  {
    id: "ev-drama-remake",
    title: "青春片翻拍惹热议：情怀是滤镜还是遮羞布",
    summary:
      "经典青春片传出翻拍消息，评论区从“爷青回”到“求放过”两极分化，翻拍到底该创新还是还原，成了最大的话题。",
    cover:
      "https://readdy.ai/api/search-image?query=Nostalgic%20youth%20school%20film%20scene%20with%20backpacks%20and%20bicycles%20in%20warm%20golden%20sunlight%2C%20memories%20and%20film%20aesthetic%2C%20cream%20amber%20tones%2C%20editorial%20illustration%2C%20no%20text&width=800&height=560&seq=ev-remake&orientation=landscape",
    cat: "drama",
    catLabel: "影视热剧",
    tags: ["翻拍", "青春片", "情怀"],
    heat: 598,
    trend: "up",
    time: "2026-09-06 12:48",
    highlight: [],
  },
  {
    id: "ev-society-travel",
    title: "假期出游数据出炉：反向旅游成新宠",
    summary:
      "长假旅游报告发布，热门目的地排名洗牌，小众反向旅游目的地上涨明显，#反向旅游真香# 登上热议榜。",
    cover:
      "https://readdy.ai/api/search-image?query=Scenic%20offbeat%20small%20town%20travel%20destination%20with%20lush%20mountains%20and%20calm%20water%20in%20warm%20morning%20light%2C%20serene%20editorial%20illustration%2C%20green%20cream%20amber%20tones%2C%20no%20text&width=800&height=560&seq=ev-society&orientation=landscape",
    cat: "society",
    catLabel: "社会热议",
    tags: ["反向旅游", "出游报告", "小众目的地"],
    heat: 542,
    trend: "up",
    time: "2026-09-06 10:33",
    highlight: [],
  },
];

export const surgeList: SurgeItem[] = [
  { title: "顶流剧组路透争议", delta: 186, catLabel: "影视热剧" },
  { title: "体育赛事黑马晋级", delta: 149, catLabel: "社会热议" },
  { title: "年度神曲舞台直拍", delta: 121, catLabel: "音乐现场" },
  { title: "新中式穿搭刮起的风", delta: 98, catLabel: "品牌话题" },
];

export const faqs: FaqItem[] = [
  {
    q: "榜单里的热度值是怎么算出来的？",
    a: "吃瓜榜单的热度值综合参考全网公开平台的讨论量、阅读量、参与互动量以及话题的持续上升速度等多维度信号，经过归一化处理后得出一个相对分值。它反映的是“事件当下被讨论的热度”，而非好坏评价。",
  },
  {
    q: "每天几点更新？会实时刷新吗？",
    a: "今日热榜会按小时滚动刷新，重点板块每天清晨进行一次完整重排。高峰期（晚间 19:00–23:00）会加密刷新频率，保证你看到的都是当下最热的瓜，而不是过期的旧闻。",
  },
  {
    q: "如何做到理性吃瓜、不传谣？",
    a: "我们坚持先核实再上榜的原则：以公开可查的信源为准，对未经证实的传闻会明确标注“待核实”。同时倡导就事论事，不参与人身攻击与网暴。上榜事件若被证伪，我们会第一时间更新并移除。",
  },
  {
    q: "上榜的事件会保留多久？",
    a: "不同事件保留周期不同：持续高热的事件会留在今日榜中，峰值过去后会自动进入“往期热议”归档。为保证阅读价值，我们会对存档事件补充后续进展，方便你按图索骥回顾整条脉络。",
  },
  {
    q: "我可以投稿或反馈一个话题吗？",
    a: "当然可以。欢迎通过页面底部邮箱与我们分享你观察到的高热话题或事件线索。投稿请尽量附上公开信源链接，我们会据此核查后再决定是否收录与排序。",
  },
];

export const principles: PrincipleItem[] = [
  {
    icon: "ri-search-line",
    title: "先核实，再上榜",
    desc: "所有事件以公开、可回溯的信源为准，无法确认的内容一律标注，绝不为了流量抢发未经证实的消息。",
  },
  {
    icon: "ri-shield-check-line",
    title: "只议热度，不判人品",
    desc: "我们关注事件为什么火、大家怎么聊，不渲染人身攻击，不把讨论引向对任何个体的网络暴力。",
  },
  {
    icon: "ri-global-line",
    title: "多方视角，交叉呈现",
    desc: "热度背后常有多个声音。同一事件我们尽量呈现不同立场，帮助你在喧嚣里形成自己的判断。",
  },
  {
    icon: "ri-refresh-line",
    title: "及时纠偏，坦诚更新",
    desc: "发现信息偏差会第一时间勘误并标注修正过程，接受批评、持续改进，把可靠放在“抢发”前面。",
  },
];

export const statCards = [
  { label: "每小时滚动刷新", value: "实时", icon: "ri-time-line" },
  { label: "日均收录热议事件", value: "120+", icon: "ri-stack-line" },
  { label: "公开信源交叉核实", value: "100%", icon: "ri-shield-check-line" },
];

export const heatColor = (h: number) => (h >= 900 ? "text-primary-500" : h >= 700 ? "text-accent-600" : "text-secondary-600");